// src/services/adminService.ts
import api from './api';
import {
  AdminOverviewResponse,
  UserAnalyticsResponse,
  EngagementAnalyticsResponse,
  SubscriptionAnalyticsResponse,
  ModerationQueueResponse,
  AuditLogsResponse,
  AuditLogFilters,
  AuditLogStatisticsResponse,
  AdminActivityResponse,
  AvailableActionsResponse,
  AuditLog,
  UserGrowthResponse,
  UserRetentionResponse,
  UserDistributionResponse,
  TopFollowersResponse,
  AlarmStatisticsResponse,
  EventAlarmDetailsResponse,
} from '../types/admin';

const ADMIN_ENDPOINTS = {
  // Dashboard
  overview: '/admin/overview',

  // Analytics
  userAnalytics: '/admin/analytics/users',
  userGrowth: '/admin/analytics/users/growth',
  userRetention: '/admin/analytics/users/retention',
  userDistribution: '/admin/analytics/users/distribution',
  topFollowers: '/admin/analytics/users/top-followers',
  engagementAnalytics: '/admin/analytics/engagement',
  subscriptionAnalytics: '/admin/analytics/subscriptions',

  // Alarms
  alarmStatistics: '/admin/analytics/alarms',
  eventAlarmDetails: (eventId: number) => `/admin/analytics/alarms/event/${eventId}`,

  // Moderation
  moderationQueue: '/admin/moderation/queue',

  // Audit Logs
  auditLogs: '/admin/audit-logs',
  auditLogDetail: (id: number) => `/admin/audit-logs/${id}`,
  adminActivity: (adminId: number) => `/admin/audit-logs/admin/${adminId}/activity`,
  auditLogStatistics: '/admin/audit-logs/statistics',
  auditLogExport: '/admin/audit-logs/export',
  availableActions: '/admin/audit-logs/actions/available',
};

// ===== DASHBOARD OVERVIEW =====
export const getAdminOverview = async (): Promise<AdminOverviewResponse> => {
  console.log('[AdminService] Fetching admin overview...');
  const response = await api.get<AdminOverviewResponse>(ADMIN_ENDPOINTS.overview);
  console.log('[AdminService] Admin overview fetched:', response.data);
  return response.data;
};

// ===== USER ANALYTICS =====
export const getUserAnalytics = async (period: number = 30): Promise<UserAnalyticsResponse> => {
  console.log('[AdminService] Fetching user analytics, period:', period);
  const response = await api.get<UserAnalyticsResponse>(ADMIN_ENDPOINTS.userAnalytics, {
    params: { period },
  });
  console.log('[AdminService] User analytics fetched:', response.data);
  return response.data;
};

export const getUserGrowth = async (
  periodDays: number = 30,
  groupBy: 'day' | 'week' | 'month' = 'day'
): Promise<UserGrowthResponse> => {
  console.log('[AdminService] Fetching user growth, period:', periodDays, 'groupBy:', groupBy);
  const response = await api.get<UserGrowthResponse>(ADMIN_ENDPOINTS.userGrowth, {
    params: { period_days: periodDays, group_by: groupBy },
  });
  console.log('[AdminService] User growth fetched:', response.data);
  return response.data;
};

export const getUserRetention = async (): Promise<UserRetentionResponse> => {
  console.log('[AdminService] Fetching user retention...');
  const response = await api.get<UserRetentionResponse>(ADMIN_ENDPOINTS.userRetention);
  console.log('[AdminService] User retention fetched:', response.data);
  return response.data;
};

export const getUserDistribution = async (): Promise<UserDistributionResponse> => {
  console.log('[AdminService] Fetching user distribution...');
  const response = await api.get<UserDistributionResponse>(ADMIN_ENDPOINTS.userDistribution);
  console.log('[AdminService] User distribution fetched:', response.data);
  return response.data;
};

export const getTopFollowers = async (
  limit: number = 50,
  includeStats: boolean = true
): Promise<TopFollowersResponse> => {
  console.log('[AdminService] Fetching top followers, limit:', limit);
  const response = await api.get<TopFollowersResponse>(ADMIN_ENDPOINTS.topFollowers, {
    params: { limit, include_stats: includeStats },
  });
  console.log('[AdminService] Top followers fetched:', response.data);
  return response.data;
};

// ===== ALARM STATISTICS =====
export const getAlarmStatistics = async (period: number = 30): Promise<AlarmStatisticsResponse> => {
  console.log('[AdminService] Fetching alarm statistics, period:', period);
  const response = await api.get<AlarmStatisticsResponse>(ADMIN_ENDPOINTS.alarmStatistics, {
    params: { period },
  });
  console.log('[AdminService] Alarm statistics fetched:', response.data);
  return response.data;
};

export const getEventAlarmDetails = async (eventId: number): Promise<EventAlarmDetailsResponse> => {
  console.log('[AdminService] Fetching event alarm details, eventId:', eventId);
  const response = await api.get<EventAlarmDetailsResponse>(ADMIN_ENDPOINTS.eventAlarmDetails(eventId));
  console.log('[AdminService] Event alarm details fetched:', response.data);
  return response.data;
};

// ===== ENGAGEMENT ANALYTICS =====
export const getEngagementAnalytics = async (period: number = 30): Promise<EngagementAnalyticsResponse> => {
  console.log('[AdminService] Fetching engagement analytics, period:', period);
  const response = await api.get<EngagementAnalyticsResponse>(ADMIN_ENDPOINTS.engagementAnalytics, {
    params: { period },
  });
  console.log('[AdminService] Engagement analytics fetched:', response.data);
  return response.data;
};

// ===== SUBSCRIPTION ANALYTICS =====
export const getSubscriptionAnalytics = async (): Promise<SubscriptionAnalyticsResponse> => {
  console.log('[AdminService] Fetching subscription analytics...');
  const response = await api.get<SubscriptionAnalyticsResponse>(ADMIN_ENDPOINTS.subscriptionAnalytics);
  console.log('[AdminService] Subscription analytics fetched:', response.data);
  return response.data;
};

// ===== MODERATION =====
export const getModerationQueue = async (): Promise<ModerationQueueResponse> => {
  console.log('[AdminService] Fetching moderation queue...');
  const response = await api.get<ModerationQueueResponse>(ADMIN_ENDPOINTS.moderationQueue);
  console.log('[AdminService] Moderation queue fetched:', response.data);
  return response.data;
};

export interface ModeratePostPayload {
  moderation_status: 'approved' | 'rejected';
  moderation_note?: string;
}

export const moderatePost = async (postId: number, payload: ModeratePostPayload): Promise<void> => {
  console.log('[AdminService] Moderating post:', postId, payload);
  await api.put(`/admin/posts/${postId}/moderate`, payload);
  console.log('[AdminService] Post moderated');
};

// ===== AUDIT LOGS =====
export const getAuditLogs = async (filters?: AuditLogFilters): Promise<AuditLogsResponse> => {
  console.log('[AdminService] Fetching audit logs:', filters);
  const response = await api.get<AuditLogsResponse>(ADMIN_ENDPOINTS.auditLogs, {
    params: filters,
  });
  console.log('[AdminService] Audit logs fetched:', response.data);
  return response.data;
};

export const getAuditLogDetail = async (logId: number): Promise<AuditLog> => {
  console.log('[AdminService] Fetching audit log detail:', logId);
  const response = await api.get<AuditLog>(ADMIN_ENDPOINTS.auditLogDetail(logId));
  console.log('[AdminService] Audit log detail fetched:', response.data);
  return response.data;
};

export const getAdminActivity = async (adminId: number, days: number = 30): Promise<AdminActivityResponse> => {
  console.log('[AdminService] Fetching admin activity:', adminId, 'days:', days);
  const response = await api.get<AdminActivityResponse>(ADMIN_ENDPOINTS.adminActivity(adminId), {
    params: { days },
  });
  console.log('[AdminService] Admin activity fetched:', response.data);
  return response.data;
};

export const getAuditLogStatistics = async (days: number = 30): Promise<AuditLogStatisticsResponse> => {
  console.log('[AdminService] Fetching audit log statistics, days:', days);
  const response = await api.get<AuditLogStatisticsResponse>(ADMIN_ENDPOINTS.auditLogStatistics, {
    params: { days },
  });
  console.log('[AdminService] Audit log statistics fetched:', response.data);
  return response.data;
};

export const exportAuditLogs = async (dateFrom: string, dateTo: string): Promise<Blob> => {
  console.log('[AdminService] Exporting audit logs:', dateFrom, 'to', dateTo);
  const response = await api.get(ADMIN_ENDPOINTS.auditLogExport, {
    params: { date_from: dateFrom, date_to: dateTo },
    responseType: 'blob',
  });
  console.log('[AdminService] Audit logs exported');
  return response.data;
};

export const getAvailableActions = async (): Promise<AvailableActionsResponse> => {
  console.log('[AdminService] Fetching available actions...');
  const response = await api.get<AvailableActionsResponse>(ADMIN_ENDPOINTS.availableActions);
  console.log('[AdminService] Available actions fetched:', response.data);
  return response.data;
};

const adminService = {
  // Dashboard
  getAdminOverview,

  // Analytics
  getUserAnalytics,
  getUserGrowth,
  getUserRetention,
  getUserDistribution,
  getTopFollowers,
  getEngagementAnalytics,
  getSubscriptionAnalytics,

  // Alarms
  getAlarmStatistics,
  getEventAlarmDetails,

  // Moderation
  getModerationQueue,
  moderatePost,

  // Audit Logs
  getAuditLogs,
  getAuditLogDetail,
  getAdminActivity,
  getAuditLogStatistics,
  exportAuditLogs,
  getAvailableActions,
};

export default adminService;
